import { TEST_IDS } from '@va/constants';
import { TestAttributes } from '@va/types/component';
import { fontWeights, GrayGradientDivider, paragraphSizes, ParagraphWithTooltip } from '@va/ui/design-system';
import { TooltipWrapper } from '@va/ui/tooltips';
import classNames from 'classnames';
import { PropsWithChildren, ReactElement, useEffect, useState } from 'react';
import Switch from 'react-switch';

export type ToggleActionCardProps = TestAttributes & {
  title?: string;
  subtitle?: string;
  handleChange?: (value: { name: string; value: boolean }) => void;
  name: string;
  value: boolean;
  icon?: () => ReactElement;
  wrapperClass?: string;
  heightClass?: string;
  titleClass?: string;
  subSectionClass?: string;
  disabled?: boolean;
  infoTooltip?: string;
  switchOnColor?: string;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  onToggleHover?: () => void;
  /** Overrides the switch wrapper test id, which defaults to `TEST_IDS.helpers.createToggleId(name)`. */
  'data-testid-switch'?: string;
  contentClassName?: string;
  endAdornment?: ReactElement;
  open?: boolean;
};

const switchSizes = {
  small: {
    width: 30,
    height: 18,
    handleDiameter: 14,
  },
  medium: {
    width: 30,
    height: 18,
    handleDiameter: 14,
  },
  large: {
    width: 48,
    height: 30,
    handleDiameter: 24,
  },
};

export const ToggleActionCard = ({
  title,
  subtitle,
  value,
  icon: Icon,
  wrapperClass,
  subSectionClass,
  contentClassName,
  name,
  handleChange,
  children,
  titleClass,
  disabled = false,
  infoTooltip,
  switchOnColor,
  className,
  onToggleHover,
  size = 'large',
  heightClass = 'min-h-60px',
  endAdornment,
  open,
  'data-testid-switch': dataTestIdSwitch,
  // Only `data-*` attributes are left, they land on the card wrapper.
  ...rest
}: PropsWithChildren<ToggleActionCardProps>) => {
  const [toggleChecked, setToggleChecked] = useState(false);

  useEffect(() => {
    setToggleChecked(value);
  }, [value]);

  const handleToggleClick = () => {
    if (handleChange) {
      handleChange({ name: name, value: !toggleChecked });
    } else {
      setToggleChecked(!toggleChecked);
    }
  };

  return (
    <div
      {...rest}
      className={classNames(
        'flex bg-white-snow justify-between items-center flex-col',
        {
          'rounded-18': size !== 'small',
          'rounded-9px': size === 'small',
        },
        wrapperClass,
      )}
    >
      <div
        className={classNames(
          'flex w-full rounded-t-18 bg-white-snow items-center gap-4u',
          { 'rounded-18': !children },
          {
            'rounded-b-18': !toggleChecked && children,
            'min-h-60px p-3': size === 'large',
            'h-42px p-3 rounded-10.5!': size === 'medium',
            'px-6u py-2u min-h-24u!': size === 'small',
          },
          heightClass,
          className,
        )}
      >
        <div
          className={classNames(
            'flex items-center truncate flex-1',
            {
              'gap-18px': size !== 'small',
              'gap-6u': size === 'small',
            },
            contentClassName,
          )}
        >
          {Icon && (
            <div className='shrink-0'>
              <Icon />
            </div>
          )}
          <div className='flex flex-col truncate'>
            {title && (
              <ParagraphWithTooltip
                weight={fontWeights.medium}
                className={classNames(titleClass, {
                  'py-1': size === 'large',
                  'leading-interface-regular text-interface-regular': size === 'small',
                })}
              >
                {title}
              </ParagraphWithTooltip>
            )}

            {subtitle && (
              <ParagraphWithTooltip
                colorClassName='text-gray-devilSolid'
                weight={fontWeights.medium}
                className={classNames('truncate text-12 leading-15', {
                  'leading-interface-small': size === 'small',
                })}
                size={paragraphSizes.tiny}
              >
                {subtitle}
              </ParagraphWithTooltip>
            )}
          </div>
          {endAdornment && <div className='ml-auto'>{endAdornment}</div>}
        </div>

        <TooltipWrapper content={infoTooltip} disabled={!infoTooltip}>
          <div
            data-testid={dataTestIdSwitch ?? TEST_IDS.helpers.createToggleId(name)}
            className='flex items-center justify-center'
            onMouseEnter={onToggleHover}
          >
            <Switch
              offHandleColor='#FFFFFF'
              onHandleColor={'#FFFFFF'}
              offColor='#C3C3C3'
              onColor={switchOnColor ?? window.POSITIVE_COLOR}
              onChange={handleToggleClick}
              checked={toggleChecked}
              checkedIcon={false}
              uncheckedIcon={false}
              disabled={disabled}
              activeBoxShadow='0px 0px 3px 4px #0000005c'
              {...switchSizes[size]}
            />
          </div>
        </TooltipWrapper>
      </div>
      {children && (toggleChecked || open) && (
        <>
          <GrayGradientDivider className='w-full' />
          <div
            className={classNames('w-full', subSectionClass, {
              'p-8u': size === 'large',
              'px-6u py-2u': size === 'small',
            })}
          >
            {children}
          </div>
        </>
      )}
    </div>
  );
};
